import React, { useState, useContext } from 'react';
import { AuthContext, API } from '@/App';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { toast } from 'sonner';
import { Loader2 } from 'lucide-react';

export const LoginPage = () => {
  const [teamMemberId, setTeamMemberId] = useState('');
  const [loading, setLoading] = useState(false);
  const { login } = useContext(AuthContext);

  const handleLogin = async (e) => {
    e.preventDefault();
    if (teamMemberId.length !== 6) {
      toast.error('Please enter a valid 6-digit Team Member ID');
      return;
    }

    setLoading(true);
    try {
      const response = await fetch(`${API}/auth/login`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ team_member_id: teamMemberId })
      });

      if (!response.ok) {
        throw new Error('Login failed');
      }

      const data = await response.json();
      login(data.token, data.user);
      toast.success('Welcome back!');
    } catch (error) {
      toast.error('Invalid Team Member ID');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="grid grid-cols-1 lg:grid-cols-2 h-screen w-full" data-testid="login-page">
      {/* Left Panel - Teal Gradient */}
      <div className="hidden lg:flex flex-col justify-center items-start bg-gradient-to-br from-teal-800 to-teal-500 text-white p-12 relative overflow-hidden">
        <img
          src="https://images.unsplash.com/photo-1697545805858-556ce0a508cc?crop=entropy&cs=srgb&fm=jpg&q=85"
          alt="Stocktake operations"
          className="absolute inset-0 w-full h-full object-cover opacity-20 mix-blend-overlay"
        />
        <div className="relative z-10 max-w-lg">
          <div className="flex items-center gap-3 mb-8">
            <div className="w-12 h-12 bg-white/20 rounded-lg flex items-center justify-center backdrop-blur-sm">
              <svg className="w-7 h-7" fill="currentColor" viewBox="0 0 24 24">
                <path d="M9 3v1H4v2h1v13a2 2 0 002 2h10a2 2 0 002-2V6h1V4h-5V3H9zm0 3h6v11H9V6z"/>
              </svg>
            </div>
            <span className="text-xl font-bold tracking-wide">INDOOROOPILLY 8197</span>
          </div>
          
          <h1 className="text-5xl font-bold mb-4 leading-tight">
            INDRO STOCKTAKE MANAGER
          </h1>
        </div>
      </div>

      {/* Right Panel - Login Form */}
      <div className="flex flex-col justify-center items-center p-8 bg-white">
        <div className="w-full max-w-md">
          <div className="mb-8">
            <div className="border-t-4 border-teal-700 w-16 mb-6"></div>
            <h2 className="text-3xl font-bold text-slate-900 mb-2">Welcome back</h2>
            <p className="text-slate-600">Sign in with your Team Member ID</p>
          </div>

          <form onSubmit={handleLogin} data-testid="login-form">
            <div className="space-y-4 mb-6">
              <div>
                <Label htmlFor="teamMemberId" className="text-sm font-semibold text-slate-700 mb-2 block">
                  Team Member ID
                </Label>
                <Input
                  id="teamMemberId"
                  type="text"
                  placeholder="Enter your 6-digit ID"
                  value={teamMemberId}
                  onChange={(e) => setTeamMemberId(e.target.value)}
                  maxLength={6}
                  className="h-12 rounded-lg border-slate-200 focus:border-teal-500 focus:ring-teal-500"
                  data-testid="team-member-id-input"
                  required
                />
              </div>
            </div>

            <Button
              type="submit"
              className="w-full h-12 bg-teal-700 hover:bg-teal-800 text-white font-medium rounded-lg shadow-sm transition-all active:scale-95"
              disabled={loading}
              data-testid="sign-in-button"
            >
              {loading ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  Signing in...
                </>
              ) : (
                'Sign In'
              )}
            </Button>
          </form>

          <p className="text-center text-sm text-slate-500 mt-8">
            Need access? Contact your store manager.
          </p>

          <p className="text-center text-xs text-slate-400 mt-12">
            © 2026 • Indooroopilly SFS
          </p>
        </div>
      </div>
    </div>
  );
};

export default LoginPage;
